import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Collapse,
  IconButton,
  styled
} from '@mui/material';
import {
  SwapHoriz as TransferIcon,
  Close as CloseIcon,
  AccountBalance as AccountIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00BCD4',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#00BCD4',
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const TransferFundsForm = ({ onSubmit, isSubmitting }) => {
  const [transferData, setTransferData] = useState({
    amount: '',
    sourceAccount: '',
    destinationAccount: '',
    notes: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTransferData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await onSubmit(transferData);
      setSuccess('Funds transferred successfully!');
      resetForm();
    } catch (err) {
      setError(err.message || 'Error transferring funds');
    }
  };

  const resetForm = () => {
    setTransferData({
      amount: '',
      sourceAccount: '',
      destinationAccount: '',
      notes: '',
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Alerts */}
        <Grid item xs={12}>
          <Collapse in={!!error}>
            <Alert 
              severity="error"
              action={
                <IconButton
                  size="small"
                  onClick={() => setError('')}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Collapse>
          
          <Collapse in={!!success}>
            <Alert 
              severity="success"
              action={
                <IconButton
                  size="small"
                  onClick={() => setSuccess('')}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {success}
            </Alert>
          </Collapse>
        </Grid>

        {/* Transfer Details */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h6" sx={{ mb: 3, color: '#00BCD4', display: 'flex', alignItems: 'center', gap: 1 }}>
              <TransferIcon />
              Transfer Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="number"
                  value={transferData.amount}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>

              <Grid item xs={12} md={5}>
                <StyledTextField
                  fullWidth
                  label="Source Account"
                  name="sourceAccount"
                  value={transferData.sourceAccount}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <AccountIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ArrowIcon sx={{ color: '#00BCD4', transform: { xs: 'rotate(90deg)', md: 'none' } }} />
              </Grid>

              <Grid item xs={12} md={5}>
                <StyledTextField
                  fullWidth
                  label="Destination Account"
                  name="destinationAccount"
                  value={transferData.destinationAccount}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <AccountIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={transferData.notes}
                  onChange={handleChange}
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#00BCD4',
              '&:hover': {
                backgroundColor: '#00ACC1',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 188, 212, 0.5)',
              }
            }}
          >
            {isSubmitting ? 'Processing Transfer...' : 'Transfer Funds'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransferFundsForm; 